import type { TimetableEntry } from '../types/timetable';
import type { EventInput } from '@fullcalendar/core';

// Map day numbers to FullCalendar day of week (0 = Sunday, 1 = Monday, etc.)
// Assuming API uses 1 = Monday, 2 = Tuesday, etc.
const mapDayToFullCalendar = (day: number): number => {
  // Convert from 1-based Monday start to 0-based Sunday start
  return day === 7 ? 0 : day;
};

// Generate a consistent color for each class based on its ID
export const getClassColor = (classId: number, isDarkMode: boolean): string => {
  const colors = isDarkMode ? [
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#45B7D1', // Blue
    '#96CEB4', // Green
    '#FFEAA7', // Yellow
    '#DDA0DD', // Plum
    '#98D8C8', // Mint
    '#FFB6C1', // Light Pink
    '#87CEEB', // Sky Blue
    '#F4A460', // Sandy Brown
  ] : [
    '#D32F2F', // Red
    '#00796B', // Teal
    '#1976D2', // Blue
    '#388E3C', // Green
    '#F57C00', // Orange
    '#7B1FA2', // Purple
    '#00838F', // Cyan
    '#C2185B', // Pink
    '#303F9F', // Indigo
    '#5D4037', // Brown
  ];
  
  return colors[classId % colors.length];
};

// Get the current week's Monday
const getWeekMonday = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
  return new Date(d.setDate(diff));
};

// Convert timetable entries to FullCalendar events for the current week
export const convertToCalendarEvents = (
  timetableEntries: TimetableEntry[],
  isDarkMode: boolean
): EventInput[] => {
  const events: EventInput[] = [];
  const weekMonday = getWeekMonday(new Date());
  
  timetableEntries.forEach((entry) => {
    const color = getClassColor(entry.id, isDarkMode);
    
    entry.days.forEach((day) => {
      const eventDate = new Date(weekMonday);
      const fcDay = mapDayToFullCalendar(day);
      
      // Calculate the date for this day of the week
      const daysToAdd = fcDay === 0 ? 6 : fcDay - 1; // Adjust for Monday start
      eventDate.setDate(weekMonday.getDate() + daysToAdd);
      
      // Parse time
      const [hours, minutes] = entry.startsAt.split(':').map(Number);
      
      // Create start time
      const startTime = new Date(eventDate);
      startTime.setHours(hours, minutes, 0, 0);
      
      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + entry.durationInMinutes);
      
      events.push({
        id: `${entry.id}-${day}`,
        title: entry.name,
        start: startTime,
        end: endTime,
        backgroundColor: color,
        borderColor: color,
        textColor: isDarkMode ? '#000000' : '#FFFFFF',
        extendedProps: {
          teacher: entry.teacher,
          course: entry.course,
          location: entry.location,
          room: entry.room,
        },
      });
    });
  });
  
  return events;
};
